import { Link } from "react-router-dom";
import { ChevronRight, Calendar, MapPin, Phone, Flag, Fish, Gift, HandHeart } from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import { activities, chapterInfo } from "../data/mock";

const iconMap = { Flag, Fish, Gift, HandHeart };

export default function Activities() {
  return (
    <main className="pt-28 pb-24">
      {/* Page header */}
      <section className="relative py-24 px-6 lg:px-10 bg-[#0A3161] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1626836014893-37663794dca7"
            alt=""
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A3161]/60 to-[#0A3161]" />
        </div>
        <div className="relative max-w-5xl mx-auto text-center">
          <SectionHeading
            align="center"
            light
            eyebrow="Chapter 240 Activities"
            title="Where The Mission Comes Alive"
            description="Tournaments, community outreach, memorials and monthly meetings. Every activity funds and fortifies our promise to Sumner County veterans."
          />
        </div>
      </section>

      {/* Activities list */}
      <section className="py-24 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto space-y-24">
          {activities.map((a, i) => {
            const Icon = iconMap[a.icon] || Flag;
            const reversed = i % 2 === 1;
            return (
              <article
                key={a.id}
                className={`grid lg:grid-cols-12 gap-10 items-center ${reversed ? "lg:[direction:rtl]" : ""}`}
              >
                <div className="lg:col-span-7 lg:[direction:ltr] relative">
                  <div className="relative overflow-hidden">
                    <img src={a.image} alt={a.title} className="w-full h-[440px] object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-tr from-[#0A3161]/50 to-transparent" />
                    <div className="absolute top-6 left-6 bg-[#B22234] w-14 h-14 flex items-center justify-center">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -bottom-8 right-8 bg-[#FFFFFF] text-[#0A3161] px-5 py-3">
                      <p className="font-condensed uppercase tracking-widest text-xs">{a.date}</p>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-5 lg:[direction:ltr]">
                  <p className="font-condensed uppercase tracking-[0.3em] text-xs text-[#B22234] mb-4">
                    {a.tagline}
                  </p>
                  <h2 className="font-display text-4xl md:text-5xl font-bold text-[#0A3161] leading-tight">
                    {a.title}
                  </h2>
                  <p className="mt-3 font-display italic text-xl text-[#B22234]">{a.subtitle}</p>
                  <p className="mt-6 text-[#1F2937] leading-relaxed">{a.description}</p>
                  <ul className="mt-6 space-y-2">
                    {a.details.map((d, k) => (
                      <li key={k} className="flex items-start gap-3 text-[#0A3161]">
                        <span className="mt-1.5 w-1.5 h-1.5 bg-[#B22234] rounded-full shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 pt-6 border-t border-[#D9DDE3] space-y-2 text-sm text-[#0A3161]/80">
                    <p className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-[#B22234]" /> {a.location}
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[#B22234]" /> {a.contact}
                    </p>
                  </div>
                  {(a.id === "golf" || a.id === "fishing") && (
                    <Link
                      to={a.link}
                      className="mt-8 inline-flex items-center gap-2 bg-[#0A3161] text-white px-7 h-12 font-condensed uppercase tracking-widest text-xs hover:bg-[#B22234] transition-colors"
                    >
                      Full Details <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Monthly meetings */}
      <section className="px-6 lg:px-10">
        <div className="max-w-6xl mx-auto bg-[#0A3161] text-white p-12 md:p-16 grid md:grid-cols-2 gap-10 items-center relative overflow-hidden">
          <div className="absolute inset-0 stars opacity-15" />
          <div className="relative">
            <p className="font-condensed uppercase tracking-[0.3em] text-xs text-[#FFFFFF] mb-4">
              Monthly Meetings
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              First Monday. Every Month.
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed">
              Join us for dinner, fellowship, and business at VFW Post #9851. A hot meal is prepared by the Associates of Vietnam Veterans of America. Members, spouses, and prospective members are always welcome.
            </p>
          </div>
          <div className="relative space-y-6">
            <div className="flex gap-4 items-start">
              <div className="w-12 h-12 bg-[#B22234] flex items-center justify-center shrink-0">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-condensed uppercase tracking-widest text-xs text-[#FFFFFF]">When</p>
                <p className="mt-1 text-lg">{chapterInfo.meetingSchedule}</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <div className="w-12 h-12 bg-[#B22234] flex items-center justify-center shrink-0">
                <MapPin className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="font-condensed uppercase tracking-widest text-xs text-[#FFFFFF]">Where</p>
                <p className="mt-1 text-lg">{chapterInfo.meetingLocation}</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
